import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Notice, Spinner, useWide } from "../../ui/index.js";
import { CabinetShell, headerBackTo, type ShellNavItem, useCabinet } from "../cabinet/index.js";

/**
 * Кабинет салона глазами админа (`/admin/s/:salonId/...`): те же страницы, что у владельца
 * (`requireSalonRole` пускает админа как владельца), плюс плашка. Вход пишется в `audit_log`.
 */
export function AdminCabinetLayout() {
  const { salonId = "" } = useParams();
  const { pathname } = useLocation();
  const cab = useCabinet();
  const wide = useWide();
  const enter = useMutation(trpc.billing.admin.enterSalon.mutationOptions());
  const { mutate } = enter;

  useEffect(() => {
    mutate({ salonId });
  }, [salonId, mutate]);

  if (enter.isError) return <Notice kind="error">Салон не найден</Notice>;
  if (!enter.data) return <Spinner />;
  const salon = { id: enter.data.salonId, name: enter.data.name, status: enter.data.status, slug: "", role: "owner" as const };

  const base = `/admin/s/${salonId}`;
  const backTo = headerBackTo(pathname, base, { isOwner: cab.isOwner, ownerKnown: !cab.loading, billingIsTab: true, wide });
  const items: ShellNavItem[] = [
    { to: `${base}/calendar`, icon: "▦", label: "Календарь" },
    { to: `${base}/conversations`, icon: "✉", label: "Диалоги" },
    { to: `${base}/settings`, icon: "☰", label: "Настройки" },
    { to: `${base}/billing`, icon: "€", label: "Подписка" },
  ];
  // Высота плашки на широком экране: одна строка text-sm с py-2 (2.25rem), см. `--shell-banner` в CabinetShell.
  const banner = (
    <div className="bg-amber-100 px-4 py-2 text-sm text-amber-900 lg:h-9 lg:truncate">
      Вы в салоне как администратор (вход записан в журнал).{" "}
      <Link to={`/admin/salons/${salonId}`} className="underline">
        Выйти
      </Link>
    </div>
  );
  return (
    <CabinetShell salonName={salon.name} items={items} backTo={backTo} banner={banner}>
      <Outlet context={{ salon }} />
    </CabinetShell>
  );
}
